import { useState } from "react";
import { Workout } from "../../libs/workout/types";
import { getIsTemplate } from "../../libs/workout/utils";
import { PlusButton } from "../Button";
import { ExerciseWizardModal } from "./ExerciseWizardModal";
import { ExerciseRow } from "./ExerciseRow";
import { SetModal } from "./SetModal";
import { getColumns, getExercisesRowsObj } from "./utils";
import { Skeleton } from "../Skeleton";
import React from "react";
import { Link } from "react-router-dom";
import { EXERCISE_TYPES } from "../../libs/exercises/types";
import { MuscleSummaryTable } from "./MuscleSummaryTable";
import { FeedbackTable } from "./FeedbackTable";
import { RecoveryChart } from "./RecoveryChart";
import { useAppDispatch } from "../../store";
import { swapDays } from "../../libs/workout/slice";
import { useGetAllExercisesQuery } from "../../libs/exercises/api";
import { buildExercisesLookup } from "./muscleUtils";

type WorkoutTableProps = {
  workout?: Workout;
  isLoading?: boolean;
  error?: any;
  isEditMode?: boolean;
  showWorkoutSetMode?: boolean;
  userId?: number;
};

export const WorkoutTables: React.FC<WorkoutTableProps> = ({
  workout,
  isLoading,
  error,
  isEditMode,
  showWorkoutSetMode,
  userId,
}) => {
  const [openSetModal, setOpenSetModal] = useState<
    | {
        dayIndex: number;
        exerciseIndex: number;
        weekIndex: number;
      }
    | undefined
  >();
  const [openExerciseModal, setOpenExerciseModal] = useState<
    | {
        dayIndex: number;
        exToCloneIndex?: number;
        exType?: keyof typeof EXERCISE_TYPES;
      }
    | undefined
  >();

  const dispatch = useAppDispatch();
  const { data: allExercises } = useGetAllExercisesQuery();
  const exercisesLookup = allExercises ? buildExercisesLookup(allExercises) : null;

  if (showWorkoutSetMode) {
    return (
      <div className="flex flex-col text-xs text-gray-700 uppercase dark:text-gray-400">
        <Link to="/workouts">
          <h2 className="font-bold center">Aggiungi un workout all'utente</h2>
          <span>Vai alla pagina dei workouts</span>
        </Link>
      </div>
    );
  }
  if (isLoading) {
    return <Skeleton />;
  }
  if (error || !workout || !workout.data) {
    return <div>Error...</div>;
  }
  const isTemplate = getIsTemplate(workout);

  const weeksColumns = workout.data.map((_, i) => "Week " + (i + 1).toString());
  const workoutDays = workout.data[0]?.days?.map((day, i) => ({
    name: day.dayNumber,
    dayIndex: i,
  }));

  return (
    <div className="flex flex-col">
      {workoutDays &&
        workoutDays.map((day, index) => (
          <div
            key={index + "-day"}
            data-workout-day={index}
            className="relative overflow-x-auto bg-white dark:bg-gray-800 rounded-2xl my-6 border border-gray-200 dark:border-gray-700 shadow-sm"
          >
            <table className="table-auto w-full text-sm text-gray-600 dark:text-gray-400 text-center">
              <thead>
                <tr>
                  <th
                    scope="col"
                    colSpan={isEditMode ? 2 : 1}
                    className="w-[400px] px-5 py-4 text-left text-lg font-bold text-gray-900 dark:text-white"
                  >
                    <div className="flex items-center gap-2">
                      <span>Day {index + 1}</span>
                      {isEditMode && workoutDays.length > 1 && (
                        <div className="inline-flex items-center gap-1 ml-2">
                          {index > 0 && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                dispatch(swapDays({ dayIndexA: index, dayIndexB: index - 1 }));
                              }}
                              className="p-1.5 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                              title="Sposta giorno su"
                            >
                              <svg className="w-4 h-4 text-gray-600 dark:text-gray-300" fill="none" viewBox="0 0 14 8">
                                <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7 7.674 1.3a.91.91 0 0 0-1.348 0L1 7" />
                              </svg>
                            </button>
                          )}
                          {index < workoutDays.length - 1 && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                dispatch(swapDays({ dayIndexA: index, dayIndexB: index + 1 }));
                              }}
                              className="p-1.5 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                              title="Sposta giorno giù"
                            >
                              <svg className="w-4 h-4 text-gray-600 dark:text-gray-300" fill="none" viewBox="0 0 14 8">
                                <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 1 5.326 5.7a.909.909 0 0 0 1.348 0L13 1" />
                              </svg>
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </th>
                  {weeksColumns.map((column) => (
                    <th
                      key={column + "-day-column-" + index}
                      scope="col"
                      colSpan={getColumns(isTemplate).length}
                      className="w-[300px] px-4 py-4 text-center text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider"
                    >
                      {column}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {getExercisesRowsObj(workout, index, exercisesLookup)?.map((exercise, i) => (
                  <ExerciseRow
                    key={i + "-exercise-row-" + index}
                    exercise={exercise}
                    dayIndex={index}
                    index={i}
                    setOpenSetModal={(weekIndex) =>
                      setOpenSetModal({
                        dayIndex: index,
                        exerciseIndex: i,
                        weekIndex: weekIndex,
                      })
                    }
                    setOpenExerciseModal={(exIndex) => {
                      const exType =
                        workout.data[0].days[index]?.exercises[exIndex]
                          ?.exercise?.type;
                      setOpenExerciseModal({
                        dayIndex: index,
                        exToCloneIndex: exIndex,
                        exType: exType,
                      });
                    }}
                    isTemplate={isTemplate}
                    isEditMode={isEditMode}
                    isLastRow={
                      i === getExercisesRowsObj(workout, index, exercisesLookup).length - 1
                    }
                    isLastDay={index === workoutDays.length - 1}
                  />
                ))}
                {isEditMode && (
                  <tr>
                    <td className="px-5 py-3">
                      <PlusButton
                        onClick={() => {
                          setOpenExerciseModal({ dayIndex: index });
                        }}
                        variant="icon"
                      />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        ))}
      <FeedbackTable workout={workout} weeksColumns={weeksColumns} />
      <MuscleSummaryTable workout={workout} weeksColumns={weeksColumns} userId={userId} />
      <RecoveryChart workout={workout} weeksColumns={weeksColumns} userId={userId} />
      <SetModal
        openModal={openSetModal}
        setOpenModal={setOpenSetModal}
        isEditMode={!!isEditMode}
        workout={workout}
      />

      <ExerciseWizardModal
        dayIndex={openExerciseModal?.dayIndex}
        exToCloneIndex={openExerciseModal?.exToCloneIndex}
        exType={openExerciseModal?.exType}
        setOpenModal={setOpenExerciseModal}
      />
    </div>
  );
};
